<script setup lang="ts">
import { watchEffect,reactive,ref, onMounted,inject,provide,computed,watch,defineAsyncComponent } from 'vue'
import { Separator } from '@/components/ui/separator'
import { Calendar, Home, Inbox, Search, Settings } from "lucide-vue-next"
import { RouterLink, RouterView,useRoute, useRouter,RouteRecordNormalized,RouteLocationNormalized } from 'vue-router'
import axios from 'axios'
import { sendGraphQLQuery } from '@/services/gql-util'
import { VhostInfo,VhostSettings  } from '@/types/Vhost'
import { Member  } from '@/types/Member'
import { TransferDestination  } from '@/types/Transfer'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { QuestionMarkCircleIcon, AdjustmentsHorizontalIcon, XMarkIcon,HomeIcon, UserCircleIcon,UserIcon, Cog6ToothIcon, CheckCircleIcon,BellIcon,HomeModernIcon, UserGroupIcon, ChevronDownIcon, } from "@heroicons/vue/24/solid";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuPortal, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger, } from '@/components/ui/dropdown-menu'
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb'
import { Switch } from '@/components/ui/switch'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, } from "@/components/ui/command"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetClose, SheetTrigger } from '@/components/ui/sheet'
import { Toaster } from '@/components/ui/sonner'
import { toast } from 'vue-sonner'
import { Popover, PopoverContent, PopoverTrigger, } from "@/components/ui/popover"
import { useCookies } from '@vueuse/integrations/useCookies';
import InjectionProvider from '@/components/InjectionProvider.vue';
import SidebarMenu from '@/components/SidebarMenu.vue';
import MobileMenu from '@/components/MobileMenu.vue';
import {getPrettyValues,FilterInfoKeys,filterInfo} from "@/data/filters";
import {Filters,defaultFilters} from "@/types/Filters";
import DynamicSearchField from '@/components/DynamicSearchField.vue'
import SimpleAccordion from '@/components/SimpleAccordion.vue'
import vhost from '@/services/Vhost';
import clientTransfer from '@/services/clientTransfer';
import permissions from "@/services/permissions";
import { updateUser } from '@/services/User'
import { isAllowedInMmMode } from '@/data/mmRoutes'
import '@vueup/vue-quill/dist/vue-quill.snow.css'

const { checkPermission  } = permissions();
const { getVhostId, getVhostInfo } = vhost();
const { transferList, getClientTransferList } = clientTransfer();
const selectedFilters = ref<(FilterInfoKeys)[]>(['filetype', 'status']);
const profileSheetOpen = ref(false);
const notificationsSheetOpen = ref(false);
const profileEditing = ref(false);
const profileSaving = ref(false);
const profileForm = ref({
	firstname: '',
	lastname: '',
	email: '',
	phone: '',
	description: '',
});

const openProfileSheet = () => {
	if (userInfo.value) {
		profileForm.value = {
			firstname: userInfo.value.firstname || '',
			lastname: userInfo.value.lastname || '',
			email: userInfo.value.email || '',
			phone: userInfo.value.phone || '',
			description: userInfo.value.description || '',
		}
		profileEditing.value = false
	}
	profileSheetOpen.value = true
}

const saveProfile = async () => {
	if (!userInfo.value) return
	profileSaving.value = true
	try {
		const updated = await updateUser(userInfo.value.id, {
			firstname: profileForm.value.firstname,
			lastname: profileForm.value.lastname,
			email: profileForm.value.email,
			phone: profileForm.value.phone,
			description: profileForm.value.description,
		})
		if (updated) {
			userInfo.value.firstname = updated.firstname
			userInfo.value.lastname = updated.lastname
			userInfo.value.email = updated.email
			userInfo.value.phone = updated.phone
			userInfo.value.description = updated.description
		}
		profileEditing.value = false
		toast.success('Profile updated')
	} catch (err) {
		console.error('Failed to update profile:', err)
		toast.error('Failed to update profile')
	} finally {
		profileSaving.value = false
	}
}
const isAccordionOpen = ref(false);
const breadCrumbTitle = inject('breadcrumb', '');
// Method to toggle accordion
const toggleAccordion = () => {
	isAccordionOpen.value = !isAccordionOpen.value;
};

const toggleSelectedFilters = (name: FilterInfoKeys) => {
	const index = selectedFilters.value.indexOf(name);

	if (index === -1) {
		// If the filter is not in the array, add it
		selectedFilters.value.push(name);
	} else {
		// If the filter is already in the array, remove it
		selectedFilters.value.splice(index, 1);
	}

	// Save updated filters to cookies if vhostId is defined
	if (vhostId && vhostId.value) {
		cookies.set('myFilters' + vhostId.value, selectedFilters.value, { maxAge: 86400 * 7 });
	}
};
const filters = reactive<Filters>({});  
const mergedFilters = computed(() => {
	return { ...defaultFilters, ...filters };
});

const theme = ref(localStorage.getItem('theme') || 'light'); // Default to light theme
const toggleTheme = () => {
	theme.value = theme.value === 'light' ? 'dark' : 'light';
	localStorage.setItem('theme', theme.value);
	applyTheme();
};
const applyTheme = () => {
	const html = document.documentElement;
	if (theme.value === 'dark') {
		html.classList.add('dark');
	} else {
		html.classList.remove('dark');
	}
};

const setFilter = (type:string) => {
	filterType.value=type;
	toggleAccordion();
};
// Type for the transition element
const beforeEnter = (el: Element) => {
	(el as HTMLElement).style.height = '0'; // Set height to 0 before entering
	(el as HTMLElement).style.overflow = 'hidden'; // Ensure content is clipped
};

const enter = (el: Element, done: () => void) => {
	const height = (el as HTMLElement).scrollHeight; // Get scrollHeight
	(el as HTMLElement).style.height = `${height}px`; // Set height to scrollHeight
	el.addEventListener('transitionend', done);
};

const leave = (el: Element, done: () => void) => {
	(el as HTMLElement).style.height = '0'; // Set height back to 0 when leaving
	el.addEventListener('transitionend', done);
};
const cookies = useCookies(['redirectUrl']);
const vhostInfo = ref<VhostInfo | null>(null);
const userInfo = ref<Member | null>(null);
const vhostId = ref<string|null>(null);
const mmMode = ref(false)
const searchTerm = ref('')
const filterType = ref('')
provide('vhostInfo', vhostInfo);
provide('transferList', transferList);
provide('userInfo', userInfo);
provide('vhostId', vhostId);
provide('mmMode', mmMode);

// Register mmMode route guard — restricts navigation when mmMode is active
let removeGuard: (() => void) | null = null
const installMmModeGuard = () => {
	if (removeGuard) removeGuard() // remove previous guard if re-installing
	removeGuard = router.beforeEach((to) => {
		if (mmMode.value && !isAllowedInMmMode(to.path)) {
			return '/403'
		}
	})
}

// Route mappings between mmMode and non-mmMode equivalents
const mmRouteMap: Record<string, string> = {
	'/media': '/assets',
	'/assets': '/media',
}

// When toggling mmMode, navigate to the equivalent page
watch(mmMode, (isMMMode) => {
	const currentPath = route.path
	if (isMMMode) {
		// Switching TO mmMode
		if (currentPath === '/media' || currentPath.startsWith('/media/')) {
			router.push('/assets')
		}
	} else {
		// Switching FROM mmMode
		if (currentPath === '/assets' || currentPath.startsWith('/assets/')) {
			router.push('/media')
		}
	}
})

const defaultFields = ref<string[]>([]);
const blockedFields = ref<string[]>([]);
// Define a reactive variable to hold the login status
const loggedin = ref(false)
const selectedProject = ref<VhostInfo | undefined | null>(undefined);
const home = ref('Home')
const pText = ref('')
const showRetired = ref(0)
const open = ref(false)
const selectedVhost = ref<string | undefined>('');
const projects = ref<VhostInfo[]>([]);

const router = useRouter();
const route = useRoute();
// Function to check if the user is logged in
const checkLoginStatus = async () => {
	try {
		const query = `
		query ($includePermissions: Boolean, $vhost: Int) {
			Users {
				getLoggedInUser(includePermissions: $includePermissions, vhost: $vhost) {
					id user firstname lastname email phone city state country postalcode
					description birthdate website address1 address2 active disabled
					created lastlogin avatar permissions meta
				}
			}
		}`;
		const data = await sendGraphQLQuery<{ Users: { getLoggedInUser: any } }>(query, {
			includePermissions: true,
			vhost: Number(vhostId.value),
		});
		const result = data?.Users?.getLoggedInUser;
		if (!result || result.id == 1) {
			loggedin.value = false;
			router.push('/login');
		} else {
			userInfo.value = result;
			loggedin.value = true;
			if (userInfo.value && vhostId.value && userInfo.value.permissions != null && checkPermission(userInfo.value.permissions[vhostId.value], 'mediafactory.mmmode')) {
				mmMode.value = true;
				installMmModeGuard();
			}
			const url = new URL(window.location.href);
			const pathAndQuery = url.pathname + url.search;
			router.push(pathAndQuery);
		}
	} catch (error) {
		console.error('Error checking login status:', error);
		loggedin.value = false;
		const url = new URL(window.location.href);
		const pathAndQuery = url.pathname + url.search;
		router.push(pathAndQuery);
	}
}
const pageDescription = computed(() => {
	let currentRoute: RouteRecordNormalized | null = route.matched[0];
	return currentRoute?.meta?.description ?? '';
});
const topComponent = computed(() => {
	let currentRoute: RouteRecordNormalized | null = route.matched[0];
	if(typeof currentRoute.meta?.topcomponent =='string') {
		return currentRoute.meta?.topcomponent;
	}
	return '';
});
const pageName = computed(() => {
  const currentRoute: RouteRecordNormalized | null = route.matched[0] ?? null;

  if (!currentRoute) return '';

  if (typeof currentRoute.meta?.breadcrumb === 'string') {
    if (mmMode && currentRoute.meta?.mmbreadcrumb) {
      return currentRoute.meta?.mmbreadcrumb;
    } else {
      return currentRoute.meta?.breadcrumb;
    }
  }

  if (typeof currentRoute.meta?.title === 'string') {
    return currentRoute.meta.title;
  }

  return '';
});
const breadcrumbs = computed(() => {
	const trail = [];
	let currentRoute: RouteRecordNormalized | null = route.matched[0]; // Start with the current route

	while (currentRoute) {
		// Retrieve breadcrumb metadata
		const breadcrumb = currentRoute.meta?.breadcrumb;
		if (breadcrumb) {
			let label= '';
			if (breadCrumbTitle =='' )  {
				label =
					typeof breadcrumb === 'function'
					? breadcrumb(route) // Function: call it with the route
					: breadcrumb; // String: use directly
			} else {
				label = breadCrumbTitle;
			}

			trail.unshift({
				label,
				path: router.resolve({ name: currentRoute.name }).href, // Resolve route path
			});
		}

		// Get parent route name from meta and resolve it
		const parentRouteName = currentRoute.meta?.parent as string | undefined;
		currentRoute = parentRouteName
			? router.getRoutes().find((r) => r.name === parentRouteName) || null
			: null;
	}

	return trail;
});
const projectSelected = (project: VhostInfo) => {
	if(project ) {
		selectedProject.value=project;
		selectedVhost.value=String(project.id);
		vhostId.value=String(project.id);
		vhostInfo.value=project;
		cookies.set('vid', String(project.id), { maxAge: 30*86400,path:"/",secure:true });
		checkLoginStatus();
	}
};
const fetchProjects = async () => {
	try {
		const query = `
		query {
			Vhost {
				getMyProjects {
					id name domain settings billingPlan created
				}
			}
		}`;
		const data = await sendGraphQLQuery<{ Vhost: { getMyProjects: VhostInfo[] } }>(query, {});
		projects.value = data?.Vhost?.getMyProjects || [];
		if (projects.value.length == 1) {
			vhostInfo.value=projects.value[0];
			vhostId.value=String(projects.value[0].id);
			selectedProject.value=projects.value[0];
			selectedVhost.value=String(projects.value[0].id);
		}
	} catch (error) {
		console.error('Failed to fetch projects:', error);
	}
};
watch( filters,
	(newFilters,oldFilters) => {
		if (vhostId && vhostId.value) {
			const serializedFilters = JSON.stringify(filters);
			cookies.set('newSparkFilters'+vhostId.value,serializedFilters,{maxAge: 86400*7});
		}
	},
	{ deep: true }
);
watch(selectedVhost, (newVhost, oldVhost) => {
	if(newVhost && newVhost!=oldVhost ) {
		selectedProject.value = projects.value.find((project) => project.id === newVhost);
		if(selectedProject && selectedProject.value) {
			vhostId.value=selectedProject.value.id;
			vhostInfo.value=selectedProject.value;
			cookies.set('vid', selectedProject.value.id, { maxAge: 30*86400,path:"/",secure:true });
		}
	}
});
// Call the function when the component is mounted
onMounted(async () => {
	applyTheme(); 
	vhostId.value = await getVhostId(); 
	await checkLoginStatus()
	await fetchProjects();

	cookies.set('redirectUrl', window.location.href, { maxAge: 3600 });
	const tmpFilters = cookies.get('newSparkFilters' + vhostId.value);
	const myFilters = cookies.get('myFilters' + vhostId.value);
	if(myFilters) {
		selectedFilters.value.length = 0; // Clear the array
		selectedFilters.value.push(...myFilters);
	}

	Object.assign(filters, {
		...structuredClone(defaultFilters),
		...tmpFilters,
	});

	if(vhostId.value) {
		try {
			const info = await getVhostInfo(vhostId.value);
			if(info && info.id) {
				vhostInfo.value = info;
				// Only update vhostId if info.id is truthy to avoid clearing it
				vhostId.value = String(info.id);
				selectedProject.value = vhostInfo.value;
				selectedVhost.value = String(info.id);
				getClientTransferList(vhostId.value);
			} else {
				// Info fetch returned no data, but keep vhostId from cookie
				console.warn('getVhostInfo returned no data for vhost:', vhostId.value);
				getClientTransferList(vhostId.value);
			}
		} catch (err) {
			console.error('Error loading vhost info:', err);
			// Keep vhostId from cookie even if info fetch fails
			getClientTransferList(vhostId.value);
		}
	}
})
</script>

<template>
	<!-- Conditionally render the nav if logged in -->
	<div v-if="loggedin && userInfo && typeof userInfo.id !='undefined' && userInfo.id!='1'" class=" bg-background">
		<div class="w-full flex flex-col md:flex-row h-screen w-screen" v-if="vhostId">
			<div  class="p-0 bg-sidebar-background text-card sticky top-0 h-screen hidden md:block" :class="{ 'mm-mode-sidebar': mmMode }">
				<SidebarMenu v-if="userInfo" :userInfo="userInfo" :vhostId="vhostId" :theme="theme"/>
			</div>
			<div class="p-0 bg-black text-card sticky top-0 w-full block md:hidden z-[100]">
				<div class="w-full flex justify-between items-center px-3 py-2">
					<img src="https://assets.newspark.ca/2/v7/gfx/newspark2025.png" class="h-6 w-auto">
					<MobileMenu/>
				</div>
			</div>
			<div class="flex-1 overflow-y-auto">
				<div class="flex flex-col ">
					<div class="flex sticky top-0 z-50 justify-between items-center w-full bg-background border-b border-sidebar-border">
						<div class="lg:pl-3 py-2 md:py-3 pl-2">
							<Breadcrumb class="hidden lg:block text-primary">
							<BreadcrumbList>
							<BreadcrumbItem>
							<BreadcrumbLink href="/"><HomeModernIcon class="h-6 text-primary"/></BreadcrumbLink>
							</BreadcrumbItem>
							<BreadcrumbSeparator />
							<BreadcrumbItem v-if="vhostId">
							<Popover v-model:open="open" class="border-none">
							<PopoverTrigger as-child>
							<Button variant="ghost" role="combobox" :aria-expanded="open" class="px-0 text-primary cursor-pointer justify-between hover:bg-background hover:text-primary"> {{ vhostId ? projects.find((project) => project.id == vhostId)?.name : "Select Project..." }}
								<ChevronDownIcon class="ml-2 h-4 w-4 shrink-0 opacity-50 "/>
							</Button>
							</PopoverTrigger>
							<PopoverContent class=" p-3 bg-card">
							<Command>
								<CommandInput class="h-9 text-primary" placeholder="Search Projects..."/>
								<CommandEmpty>No Project found.</CommandEmpty>
								<CommandList>
								<CommandGroup>
								<template v-for="project in projects"  :key="project.id">
									<CommandItem v-if="!project.name.includes('(RETIRED)')" :value="project.name"  @select="projectSelected(project)">
									<div class="flex w-full justify-between items-center">
										<div>
											{{ project.name }} ({{project.id}})
										</div>
										<div class="flex justify-end">
											<CheckCircleIcon v-if="vhostId == project.id" class="h-6 text-primary "/>
										</div>
									</div>
									</CommandItem>
								</template>
								</CommandGroup>
								</CommandList>
							</Command>
							</PopoverContent>
							</Popover>
							</BreadcrumbItem>
							<BreadcrumbSeparator />
							<BreadcrumbItem v-for="(crumb, index) in breadcrumbs" :key="index">
							<BreadcrumbLink asChild>
							<router-link :to="crumb.path" class="text-primary">{{ crumb.label }}</router-link>
							</BreadcrumbLink>

							<BreadcrumbSeparator v-if="index < breadcrumbs.length - 1"/>
							</BreadcrumbItem>
							</BreadcrumbList>
							</Breadcrumb>
						</div>
						<div class="grow" v-if="vhostInfo!=null && vhostInfo.id">
							<DynamicSearchField :vhost="vhostInfo.id" v-model="searchTerm"  type="media" @on-filter="setFilter"></DynamicSearchField>

						</div>
						<div class="text-muted-foreground flex space-x-1 md:space-x-3 pr-2 md:pr-3 py-2 md:py-3 items-center">
							<div class="flex items-center space-x-1 md:space-x-2 p-1">
								<div @click="toggleTheme" class="cursor-pointer">
									<QuestionMarkCircleIcon class="h-6 w-6 md:h-8 md:w-8 hover:text-primary"/>
								</div>
								<div class="flex items-center space-x-1 pl-1 border-l border-border ml-1">
									<label class="text-xs text-muted-foreground whitespace-nowrap cursor-pointer select-none">MM</label>
									<Switch
										:checked="mmMode"
										@update:checked="(val: boolean) => { mmMode = val; if (val) { installMmModeGuard(); } else if (removeGuard) { removeGuard(); removeGuard = null; } }"
										class="scale-75"
									/>
								</div>
							</div>
							<button @click="openProfileSheet" class="cursor-pointer p-1">
								<UserCircleIcon class="h-6 w-6 md:h-8 md:w-8 hover:text-primary"/>
							</button>
							<button @click="notificationsSheetOpen = true" class="cursor-pointer p-1">
								<div class="flex justify-start items-start">
									<div>
										<BellIcon class="h-6 w-6 md:h-8 md:w-8 hover:text-primary"/>
									</div>
									<div>
										<span class="relative flex h-3 w-3">
											<span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75"></span>
											<span class="relative inline-flex rounded-full h-3 w-3 bg-sky-500"></span>
										</span>
									</div>
								</div>
							</button>
						</div>
					</div>
					<div>
						<!-- Button to toggle accordion -->

						<!-- Accordion Component -->
						<SimpleAccordion v-model="isAccordionOpen" :class="{'border-b':isAccordionOpen}">
						<div  class="bg-card  overflow-hidden">
							<div class="flex justify-between items-center">
								<div class="lg:flex w-full px-2 hidden items-center justify-start space-x-3 py-1">
									<div v-for="filterName in selectedFilters">
										<div v-if="filterInfo[filterName]"> 
											<div v-if="filterInfo[filterName]['type']=='single' && typeof mergedFilters[filterName] == 'string'">
												<div v-if="Object.keys(filterInfo[filterName]['options']).length>0">
													<Select v-model="filters[filterName] as string | undefined" class="mb-2 bg-card">
														<SelectTrigger class="h-9 bg-card">
														<SelectValue :placeholder="filterInfo[filterName]['label']"/>
														</SelectTrigger>
														<SelectContent>
														<template v-for="(label,value,index) in filterInfo[filterName]['options']">
															<SelectItem :value="String(value)">{{label}}</SelectItem>
														</template>
														</SelectContent>
													</Select>
												</div>

											</div>
											<div v-else-if="filterInfo[filterName]['type']=='text'">
												<Input v-model="filters[filterName] as string | undefined" :placeholder="filterInfo[filterName]['label']" class="h-9 bg-card border border-sidebar-border"/>
											</div>
											<div v-if="filterInfo[filterName]['type']=='multiple'">
												<Popover>
												<PopoverTrigger as-child>
												<Button variant="outline" class=" justify-start bg-card">
													<component  :is="filterInfo[filterName]['icon']"/>
													{{filterInfo[filterName]['label']}}<ChevronDownIcon/>
												</Button>
												</PopoverTrigger>
												<PopoverContent class=" p-4">
												<div class="space-y-2">
													<label class="flex items-center" v-for="(label,value,index) in filterInfo[filterName]['options']">
														<input type="checkbox" :value="value" v-model="filters[filterName]" class="mr-2"/>{{label}}
													</label>
												</div>
												</PopoverContent>
												</Popover>
											</div>
										</div>
									</div>
								</div>
								<div class="px-3">
									<DropdownMenu>
									<DropdownMenuTrigger>
									<AdjustmentsHorizontalIcon class="w-9 h-9 border rounded "/>
									</DropdownMenuTrigger>
									<DropdownMenuContent>
									<DropdownMenuLabel class="font-normal">Available Filters</DropdownMenuLabel>
									<DropdownMenuSeparator/> <DropdownMenuItem v-for="(filter, name) in filterInfo" :key="name" class="py-3 flex justify-between" @click.prevent="toggleSelectedFilters(name)" >
										  <div> <component :is="filter.icon" class="h-5 w-5 inline"/> {{ filter.label }} </div>
									  <div> <CheckCircleIcon class="inline w-5 h-5" :class="{ 'text-primary': selectedFilters.includes(name) }" /> </div>
									</DropdownMenuItem>
									</DropdownMenuContent>
									</DropdownMenu>

								</div>
							</div>

						</div>
						<div class=" items-center flex flex-wrap pl-6 justify-start grow">
							<div v-for="(value,label,index) in filterInfo" class="text-body-sm mt-1">
								<div v-if="getPrettyValues(label,filters)!=''" class="px-2">
									<Button variant="outline" class="round-xl">{{getPrettyValues(label,filters)}}
										<div class="pl-2 "> <a href="#" @click.prevent="console.log('remove filter')" ><XMarkIcon class="h-4 w-4"/></a></div>
									</Button>
								</div>

							</div>
						</div>

						</SimpleAccordion>
					</div>
					<div class="flex flex-col w-full h-screen space-y-1 md:space-y-2">
						<div class="lg:space-y-3 p-2 shrink-0" v-if="pageName!='' && !route.meta.hidePageHeader" >
							<div class="space-y-0.5">
								<h2 class="text-lg md:text-headline font-light pl-2 md:pl-4">
									{{pageName}}
								</h2>
							<p class="text-muted-foreground text-body-sm hidden lg:block pl-4">
							{{pageDescription}}
							</p>
							</div>
						</div>
						<div class="flex-1 min-h-0">
							<RouterView :key="vhostId" :vhost="vhostId" :filters="filters"/>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div v-else> 
			<div class="flex items-center justify-center h-screen">
				<div class="w-1/2 h-1/4 border border-gray-400 p-8 rounded-xl space-y-5">
					<label class="text-subtitle ">What project do you want to work on today?</label>
					<Select v-model="selectedVhost" class="mt-5">
						<SelectTrigger>
						<SelectValue placeholder="Select Project"/>
						</SelectTrigger>
						<SelectContent>
						<SelectItem v-for="project in projects"  :key="project.id" :value="project.id"> {{project.name}} ({{project.id}}) </SelectItem>
						</SelectContent>
					</Select>

				</div>
			</div>


		</div>

	</div>
	<div v-else>
		<RouterView/>
	</div>
	<Toaster />

	<!-- Profile Sheet -->
	<Sheet v-model:open="profileSheetOpen">
		<SheetContent side="right" class="w-full sm:w-[380px] md:w-[420px] sm:max-w-[420px] overflow-y-auto p-0">
			<div v-if="userInfo" class="flex flex-col h-full">
				<!-- Header with avatar -->
				<div class="bg-primary/5 px-6 pt-10 pb-6">
					<div class="flex items-center gap-4">
						<div class="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
							<UserCircleIcon class="h-10 w-10 text-primary"/>
						</div>
						<div class="min-w-0">
							<p class="font-semibold text-base truncate">{{ userInfo.firstname }} {{ userInfo.lastname }}</p>
							<p class="text-sm text-muted-foreground truncate">{{ userInfo.user }}</p>
						</div>
					</div>
				</div>

				<!-- View mode -->
				<div v-if="!profileEditing" class="flex-1">
					<div class="px-6 py-4 space-y-1">
						<div class="flex items-center justify-between">
							<p class="text-xs font-semibold uppercase text-muted-foreground tracking-wider">Account</p>
							<button @click="profileEditing = true" class="text-xs text-primary hover:underline">Edit</button>
						</div>
						<div class="space-y-3 pt-1">
							<div class="flex justify-between items-center">
								<span class="text-sm text-muted-foreground">Email</span>
								<span class="text-sm font-medium truncate ml-4">{{ userInfo.email }}</span>
							</div>
							<div class="flex justify-between items-center">
								<span class="text-sm text-muted-foreground">First Name</span>
								<span class="text-sm font-medium truncate ml-4">{{ userInfo.firstname }}</span>
							</div>
							<div class="flex justify-between items-center">
								<span class="text-sm text-muted-foreground">Last Name</span>
								<span class="text-sm font-medium truncate ml-4">{{ userInfo.lastname }}</span>
							</div>
							<div class="flex justify-between items-center">
								<span class="text-sm text-muted-foreground">Phone</span>
								<span class="text-sm font-medium truncate ml-4">{{ userInfo.phone || '—' }}</span>
							</div>
							<div v-if="userInfo.description" class="pt-1">
								<span class="text-sm text-muted-foreground">Bio</span>
								<p class="text-sm mt-1">{{ userInfo.description }}</p>
							</div>
						</div>
					</div>

					<Separator />

					<div class="px-6 py-4 space-y-1">
						<p class="text-xs font-semibold uppercase text-muted-foreground tracking-wider">Preferences</p>
						<div class="flex items-center justify-between pt-1">
							<p class="text-sm">Dark Mode</p>
							<Switch :checked="theme === 'dark'" @update:checked="toggleTheme" />
						</div>
					</div>

					<Separator />

					<div class="px-6 py-4 space-y-2 mt-auto">
						<Button variant="outline" class="w-full justify-start" @click="profileSheetOpen = false; router.push('/profile')">
							<Cog6ToothIcon class="h-4 w-4 mr-2"/> Full Account Settings
						</Button>
						<Button variant="ghost" class="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10" @click="router.push('/login')">
							Log Out
						</Button>
					</div>
				</div>

				<!-- Edit mode -->
				<div v-else class="flex-1 px-6 py-4 space-y-4">
					<div class="flex items-center justify-between">
						<p class="text-xs font-semibold uppercase text-muted-foreground tracking-wider">Edit Profile</p>
						<button @click="profileEditing = false" class="text-xs text-muted-foreground hover:underline">Cancel</button>
					</div>
					<div class="space-y-3">
						<div class="space-y-1">
							<label class="text-xs font-medium text-muted-foreground">First Name</label>
							<Input v-model="profileForm.firstname" class="h-9"/>
						</div>
						<div class="space-y-1">
							<label class="text-xs font-medium text-muted-foreground">Last Name</label>
							<Input v-model="profileForm.lastname" class="h-9"/>
						</div>
						<div class="space-y-1">
							<label class="text-xs font-medium text-muted-foreground">Email</label>
							<Input v-model="profileForm.email" type="email" class="h-9"/>
						</div>
						<div class="space-y-1">
							<label class="text-xs font-medium text-muted-foreground">Phone</label>
							<Input v-model="profileForm.phone" class="h-9"/>
						</div>
						<div class="space-y-1">
							<label class="text-xs font-medium text-muted-foreground">Bio</label>
							<textarea v-model="profileForm.description" rows="3" class="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"/>
						</div>
					</div>
					<Button class="w-full" :disabled="profileSaving" @click="saveProfile">
						{{ profileSaving ? 'Saving...' : 'Save Changes' }}
					</Button>
				</div>
			</div>
		</SheetContent>
	</Sheet>

	<!-- Notifications Sheet -->
	<Sheet v-model:open="notificationsSheetOpen">
		<SheetContent side="right" class="w-full sm:max-w-md overflow-y-auto">
			<SheetHeader>
				<SheetTitle>Notifications</SheetTitle>
				<SheetDescription>Stay up to date with activity in your projects.</SheetDescription>
			</SheetHeader>
			<div class="py-4 space-y-3">
				<div class="flex items-start gap-3 p-3 rounded-lg bg-primary/5 border border-primary/10">
					<div class="mt-0.5">
						<span class="relative flex h-2.5 w-2.5">
							<span class="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary"></span>
						</span>
					</div>
					<div class="flex-1 space-y-1">
						<p class="text-sm font-medium">Welcome to NewSpark</p>
						<p class="text-xs text-muted-foreground">Your workspace is ready. Start by uploading media or exploring your projects.</p>
						<p class="text-xs text-muted-foreground">Just now</p>
					</div>
				</div>
				<div class="flex items-center justify-center py-8 text-muted-foreground">
					<div class="text-center space-y-2">
						<BellIcon class="h-10 w-10 mx-auto opacity-30"/>
						<p class="text-sm">You're all caught up!</p>
					</div>
				</div>
			</div>
		</SheetContent>
	</Sheet>

	<!-- Always render the RouterView to display the current route's component -->
</template>

<style scoped>
body {
	font-family: "Roboto";
}
/* Add any scoped CSS styles for your component here */
</style>
